import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { theme } from '../styles/theme';
import { emailService } from '../services/emailService';
import { SharedLayout } from '../components/layout/SharedLayout';

const BacktesterWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  display: flex;
  flex-direction: column;
  
  /* Override SharedLayout's MainContent padding for this special page */
  && {
    padding: 0;
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 20px 60px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const ContentCard = styled.div`
  background: #2C3E50;
  border-radius: 20px;
  padding: 60px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 600px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 40px 30px;
  }
`;

const Title = styled.h1`
  color: white;
  font-size: 2.5rem;
  line-height: 1.2;
  margin-bottom: 1rem;
  font-weight: 700;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: #B8C6DB;
  font-size: 1.2rem;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  
  strong {
    color: white;
    font-weight: 600;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #E2E8F0;
  font-size: 1.1rem;
  font-weight: 500;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  
  &:focus-within {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }
`;

const DollarSign = styled.span`
  position: absolute;
  left: 20px;
  color: #64748B;
  font-size: 1.3rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 18px 20px 18px 45px;
  font-size: 1.2rem;
  border: none;
  background: transparent;
  color: #1e293b;
  outline: none;
  
  &::placeholder {
    color: #94A3B8;
  }
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const EmailInput = styled(Input)`
  padding: 18px 20px;
  margin-bottom: 0.5rem;
`;

const SubmitButton = styled.button`
  background: #10B981;
  color: white;
  padding: 18px;
  font-size: 1.2rem;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(16, 185, 129, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  text-align: center;
  color: #94A3B8;
  font-size: 1rem;
  margin: 2rem 0;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 40%;
    height: 1px;
    background: #475569;
  }
  
  &::before {
    left: 0;
  }
  
  &::after {
    right: 0;
  }
`;

const AlternativeSection = styled.div`
  text-align: center;
  padding-top: 1rem;
`;

const AlternativeText = styled.p`
  color: #B8C6DB;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
`;

const AlternativeButton = styled.button`
  background: transparent;
  color: #60A5FA;
  padding: 14px 28px;
  font-size: 1.1rem;
  font-weight: 600;
  border: 2px solid #60A5FA;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #60A5FA;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(96, 165, 250, 0.3);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 18px 20px;
  font-size: 1.2rem;
  border: none;
  background: white;
  color: #1e293b;
  outline: none;
  border-radius: 10px;
  cursor: pointer;
  
  &:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 18px 20px;
  font-size: 1.1rem;
  border: none;
  background: white;
  color: #1e293b;
  outline: none;
  border-radius: 10px;
  resize: vertical;
  min-height: 100px;
  
  &::placeholder {
    color: #94A3B8;
  }
  
  &:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  color: #B8C6DB;
  font-size: 1rem;
  font-weight: 500;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #475569;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: linear-gradient(90deg, #10B981 0%, #34D399 100%);
  transition: width 0.3s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 1.5rem;
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: white;
  padding: 14px 28px;
  font-size: 1.1rem;
  font-weight: 600;
  border: 2px solid #64748B;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #475569;
    border-color: #94A3B8;
  }
`;

const ResultsSection = styled.div`
  background: rgba(16, 185, 129, 0.1);
  padding: 2rem;
  border-radius: 12px;
  margin-top: 2rem;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ResultCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 8px;
  backdrop-filter: blur(10px);
`;

const ResultLabel = styled.div`
  font-size: 0.9rem;
  color: #94A3B8;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ResultValue = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  color: #10B981;
`;

const PhoneInput = styled(Input)`
  padding: 18px 20px;
`;

const SuccessMessage = styled.div`
  background: #10B981;
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  font-size: 1.1rem;
  margin-top: 1rem;
`;

const ErrorMessage = styled.div`
  background: #EF4444;
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  font-size: 1.1rem;
  margin-top: 1rem;
`;


interface FormData {
  // Step 1: Personal Information
  fullName: string;
  email: string;
  phone: string;
  currentAge: string;
  retirementAge: string;
  
  // Step 2: Financial Information
  currentSavings: string;
  monthlyContribution: string;
  expectedReturn: string;
  inflationRate: string;
  
  // Step 3: Retirement Goals
  monthlyRetirementIncome: string;
  retirementLifestyle: string;
  healthcareCosts: string;
  additionalGoals: string;
  
  // Original Backtester field
  investmentAmount: string;
}

interface CalculationResults {
  retirementNeeded: number;
  projectedSavings: number;
  savingsGap: number;
  recommendedMonthly: number;
  yearsToRetirement: number;
  inflationAdjustedGoal: number;
}

const BacktesterFooter = styled.footer`
  background: #1a202c;
  color: #718096;
  text-align: center;
  padding: 30px 20px;
  font-size: 0.9rem;
  
  a {
    color: #60A5FA;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const AnnuityBacktester: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formMode, setFormMode] = useState<'backtester' | 'calculator'>('backtester');
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    currentAge: '',
    retirementAge: '',
    currentSavings: '',
    monthlyContribution: '',
    expectedReturn: '7',
    inflationRate: '3',
    monthlyRetirementIncome: '',
    retirementLifestyle: 'moderate',
    healthcareCosts: '',
    additionalGoals: '',
    investmentAmount: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [results, setResults] = useState<CalculationResults | null>(null);
  
  const totalSteps = formMode === 'calculator' ? 3 : 1;
  const progress = (currentStep / totalSteps) * 100;

  const validateStep = () => {
    const newErrors: Partial<FormData> = {};
    
    if (formMode === 'backtester') {
      if (!formData.investmentAmount) newErrors.investmentAmount = 'Investment amount is required';
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
    
    if (currentStep === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.currentAge) newErrors.currentAge = 'Current age is required';
      if (!formData.retirementAge) newErrors.retirementAge = 'Retirement age is required';
      if (Number(formData.retirementAge) <= Number(formData.currentAge)) {
        newErrors.retirementAge = 'Retirement age must be greater than current age';
      }
    }
    
    if (currentStep === 2) {
      if (!formData.currentSavings) newErrors.currentSavings = 'Current savings is required';
      if (!formData.monthlyContribution) newErrors.monthlyContribution = 'Monthly contribution is required';
    }
    
    if (currentStep === 3) {
      if (!formData.monthlyRetirementIncome) newErrors.monthlyRetirementIncome = 'Monthly income goal is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        calculateResults();
      }
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const calculateResults = () => {
    const currentAge = Number(formData.currentAge);
    const retirementAge = Number(formData.retirementAge);
    const currentSavings = Number(formData.currentSavings);
    const monthlyContribution = Number(formData.monthlyContribution);
    const expectedReturn = Number(formData.expectedReturn) / 100;
    const inflationRate = Number(formData.inflationRate) / 100;
    const monthlyRetirementIncome = Number(formData.monthlyRetirementIncome);
    const healthcareCosts = Number(formData.healthcareCosts) || 500;
    
    const yearsToRetirement = retirementAge - currentAge;
    const monthsToRetirement = yearsToRetirement * 12;
    const yearsInRetirement = 30;
    
    const futureValueCurrentSavings = currentSavings * Math.pow(1 + expectedReturn, yearsToRetirement);
    const monthlyReturn = expectedReturn / 12;
    const futureValueContributions = monthlyContribution * 
      ((Math.pow(1 + monthlyReturn, monthsToRetirement) - 1) / monthlyReturn);
    
    const projectedSavings = futureValueCurrentSavings + futureValueContributions;
    
    const totalMonthlyNeeded = monthlyRetirementIncome + healthcareCosts;
    const inflationAdjustedMonthly = totalMonthlyNeeded * Math.pow(1 + inflationRate, yearsToRetirement);
    const inflationAdjustedGoal = inflationAdjustedMonthly * 12 * yearsInRetirement;
    
    const savingsGap = Math.max(0, inflationAdjustedGoal - projectedSavings);
    
    let recommendedMonthly = monthlyContribution;
    if (savingsGap > 0) {
      recommendedMonthly = (savingsGap / ((Math.pow(1 + monthlyReturn, monthsToRetirement) - 1) / monthlyReturn)) + monthlyContribution;
    }
    
    const calculatedResults: CalculationResults = {
      retirementNeeded: inflationAdjustedGoal,
      projectedSavings,
      savingsGap,
      recommendedMonthly,
      yearsToRetirement,
      inflationAdjustedGoal: inflationAdjustedMonthly
    };
    
    setResults(calculatedResults);
    sendResults(calculatedResults);
  };
  
  const sendResults = async (calculatedResults?: CalculationResults) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      if (formMode === 'backtester') {
        const emailData = {
          data: {
            from_name: 'Website Visitor',
            from_email: formData.email || 'not-provided@example.com',
            phone: 'Not provided',
            insurance_company: 'Not provided',
            annuity_product: 'Backtesting Tool Request',
            year_purchased: 'N/A',
            current_age: 'N/A',
            initial_investment: `$${formData.investmentAmount}`,
            primary_goal: 'AI Backtesting Analysis',
            message: `
New Annuity Backtester Request

Investment Amount: $${formData.investmentAmount}
Contact Email: ${formData.email || 'Not provided'}

User is interested in seeing what they could have made with a FREE AI backtest.

Submitted via: Annuity Backtester Tool
Submitted on: ${new Date().toLocaleString()}
            `.trim()
          }
        };
        
        const apiUrl = process.env.REACT_APP_API_URL || 'https://www.annuityanswers.com/api/send-email';
        const response = await axios.post(apiUrl, emailData);
        
        if (response.status === 200) {
          setSuccess(true);
          setFormData(prev => ({ ...prev, investmentAmount: '', email: '' }));
          setTimeout(() => {
            setSuccess(false);
          }, 5000);
        } else {
          throw new Error('Failed to send request');
        }
      } else if (calculatedResults) {
        const emailData = {
          to_email: 'jake@averillagencies.com',
          from_name: formData.fullName,
          from_email: formData.email,
          phone: formData.phone,
          message: `
Retirement Calculator Lead Submission

CONTACT INFORMATION:
Name: ${formData.fullName}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}

PERSONAL DETAILS:
Current Age: ${formData.currentAge}
Retirement Age: ${formData.retirementAge}
Years to Retirement: ${calculatedResults.yearsToRetirement}

FINANCIAL INFORMATION:
Current Savings: $${Number(formData.currentSavings).toLocaleString()}
Monthly Contribution: $${Number(formData.monthlyContribution).toLocaleString()}
Expected Annual Return: ${formData.expectedReturn}%
Inflation Rate: ${formData.inflationRate}%

RETIREMENT GOALS:
Desired Monthly Income: $${Number(formData.monthlyRetirementIncome).toLocaleString()}
Retirement Lifestyle: ${formData.retirementLifestyle}
Estimated Healthcare Costs: $${formData.healthcareCosts || '500'}/month
Additional Goals: ${formData.additionalGoals || 'None specified'}

CALCULATED RESULTS:
Total Retirement Needed: $${calculatedResults.retirementNeeded.toLocaleString()}
Projected Savings at Retirement: $${calculatedResults.projectedSavings.toLocaleString()}
Savings Gap: $${calculatedResults.savingsGap.toLocaleString()}
Recommended Monthly Contribution: $${calculatedResults.recommendedMonthly.toLocaleString()}
Inflation-Adjusted Monthly Goal: $${calculatedResults.inflationAdjustedGoal.toLocaleString()}

Submission Date: ${new Date().toLocaleString()}
          `.trim()
        };
        
        await emailService.sendViaAPI(emailData);
        setSuccess(true);
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setError('There was an issue submitting your request. Our team has been notified. Please try again later.');
      
      // No mailto fallback - just show error message
      // Backend should have already sent notification to jake@averillagencies.com
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formMode === 'backtester') {
      if (validateStep()) {
        await sendResults();
      }
    } else {
      handleNext();
    }
  };

  const handleCalculatorClick = () => {
    setFormMode('calculator');
    setCurrentStep(1);
    setResults(null);
    setSuccess(false);
    setError('');
  };
  
  const switchToBacktester = () => {
    setFormMode('backtester');
    setCurrentStep(1);
    setResults(null);
    setSuccess(false);
    setError('');
  };
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <SharedLayout>
      <BacktesterWrapper>
        <MainContent>
          <ContentCard>
          {formMode === 'backtester' ? (
            <>
              <Title>Which annuity allocation method performs best?</Title>
              <Subtitle>
                Use our proprietary AI backtester free of cost - <strong>don't purchase an annuity without checking its propensity for return.</strong>
              </Subtitle>

              <Form onSubmit={handleSubmit}>
                <InputGroup>
                  <Label>Enter your total investment amount:</Label>
                  <InputWrapper>
                    <DollarSign>$</DollarSign>
                    <Input
                      type="number"
                      placeholder="Investment amount"
                      name="investmentAmount"
                      value={formData.investmentAmount}
                      onChange={handleInputChange}
                      required
                      min="1"
                      step="1"
                    />
                  </InputWrapper>
                  {errors.investmentAmount && <ErrorMessage>{errors.investmentAmount}</ErrorMessage>}
                </InputGroup>

                <InputGroup>
                  <Label>Your email (optional - for receiving results):</Label>
                  <InputWrapper>
                    <EmailInput
                      type="email"
                      placeholder="your@email.com"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </InputWrapper>
                </InputGroup>

                <SubmitButton type="submit" disabled={loading || !formData.investmentAmount}>
                  {loading ? 'SUBMITTING...' : 'SEE WHAT YOU COULD HAVE MADE (FREE AI BACKTEST)'}
                </SubmitButton>
              </Form>

              {success && (
                <SuccessMessage>
                  Your request has been submitted successfully! We'll analyze your investment and get back to you shortly.
                </SuccessMessage>
              )}

              {error && (
                <ErrorMessage>
                  {error}
                </ErrorMessage>
              )}

              <Divider>OR</Divider>

              <AlternativeSection>
                <AlternativeText>Not sure if you can retire?</AlternativeText>
                <AlternativeButton onClick={handleCalculatorClick}>
                  Try the retirement calculator
                </AlternativeButton>
              </AlternativeSection>
            </>
          ) : (
            <>
              <Title>Retirement Planning Calculator</Title>
              <Subtitle>Get a personalized retirement plan in just 3 steps</Subtitle>
              
              <ProgressBar>
                <ProgressFill progress={progress} />
              </ProgressBar>
              
              <StepIndicator>
                Step {currentStep} of {totalSteps}
              </StepIndicator>
              
              {!results ? (
                <Form onSubmit={handleSubmit}>
                  {/* Step 1: Personal Information */}
                  {currentStep === 1 && (
                    <>
                      <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>Personal Information</h2>
                      
                      <InputGroup>
                        <Label>Full Name *</Label>
                        <InputWrapper>
                          <Input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            placeholder="John Smith"
                            style={{ paddingLeft: '20px' }}
                          />
                        </InputWrapper>
                        {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Email Address *</Label>
                        <InputWrapper>
                          <EmailInput
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="john@example.com"
                          />
                        </InputWrapper>
                        {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Phone Number</Label>
                        <InputWrapper>
                          <PhoneInput
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="(555) 123-4567"
                          />
                        </InputWrapper>
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Current Age *</Label>
                        <InputWrapper>
                          <Input
                            type="number"
                            name="currentAge"
                            value={formData.currentAge}
                            onChange={handleInputChange}
                            placeholder="45"
                            min="18"
                            max="100"
                            style={{ paddingLeft: '20px' }}
                          />
                        </InputWrapper>
                        {errors.currentAge && <ErrorMessage>{errors.currentAge}</ErrorMessage>}
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Desired Retirement Age *</Label>
                        <InputWrapper>
                          <Input
                            type="number"
                            name="retirementAge"
                            value={formData.retirementAge}
                            onChange={handleInputChange}
                            placeholder="65"
                            min="50"
                            max="100"
                            style={{ paddingLeft: '20px' }}
                          />
                        </InputWrapper>
                        {errors.retirementAge && <ErrorMessage>{errors.retirementAge}</ErrorMessage>}
                      </InputGroup>
                    </>
                  )}
                  
                  {/* Step 2: Financial Information */}
                  {currentStep === 2 && (
                    <>
                      <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>Financial Information</h2>
                      
                      <InputGroup>
                        <Label>Current Retirement Savings *</Label>
                        <InputWrapper>
                          <DollarSign>$</DollarSign>
                          <Input
                            type="number"
                            name="currentSavings"
                            value={formData.currentSavings}
                            onChange={handleInputChange}
                            placeholder="100000"
                            min="0"
                          />
                        </InputWrapper>
                        {errors.currentSavings && <ErrorMessage>{errors.currentSavings}</ErrorMessage>}
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Monthly Contribution *</Label>
                        <InputWrapper>
                          <DollarSign>$</DollarSign>
                          <Input
                            type="number"
                            name="monthlyContribution"
                            value={formData.monthlyContribution}
                            onChange={handleInputChange}
                            placeholder="500"
                            min="0"
                          />
                        </InputWrapper>
                        {errors.monthlyContribution && <ErrorMessage>{errors.monthlyContribution}</ErrorMessage>}
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Expected Annual Return (%)</Label>
                        <Select
                          name="expectedReturn"
                          value={formData.expectedReturn}
                          onChange={handleInputChange}
                        >
                          <option value="5">5% (Conservative)</option>
                          <option value="7">7% (Moderate)</option>
                          <option value="9">9% (Aggressive)</option>
                          <option value="11">11% (Very Aggressive)</option>
                        </Select>
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Expected Inflation Rate (%)</Label>
                        <Select
                          name="inflationRate"
                          value={formData.inflationRate}
                          onChange={handleInputChange}
                        >
                          <option value="2">2% (Low)</option>
                          <option value="3">3% (Average)</option>
                          <option value="4">4% (High)</option>
                          <option value="5">5% (Very High)</option>
                        </Select>
                      </InputGroup>
                    </>
                  )}
                  
                  {/* Step 3: Retirement Goals */}
                  {currentStep === 3 && (
                    <>
                      <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>Retirement Goals</h2>
                      
                      <InputGroup>
                        <Label>Desired Monthly Income in Retirement *</Label>
                        <InputWrapper>
                          <DollarSign>$</DollarSign>
                          <Input
                            type="number"
                            name="monthlyRetirementIncome"
                            value={formData.monthlyRetirementIncome}
                            onChange={handleInputChange}
                            placeholder="5000"
                            min="0"
                          />
                        </InputWrapper>
                        {errors.monthlyRetirementIncome && <ErrorMessage>{errors.monthlyRetirementIncome}</ErrorMessage>}
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Retirement Lifestyle</Label>
                        <Select
                          name="retirementLifestyle"
                          value={formData.retirementLifestyle}
                          onChange={handleInputChange}
                        >
                          <option value="basic">Basic - Essential expenses only</option>
                          <option value="moderate">Moderate - Comfortable with some extras</option>
                          <option value="active">Active - Travel and hobbies</option>
                          <option value="luxury">Luxury - Premium lifestyle</option>
                        </Select>
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Estimated Monthly Healthcare Costs</Label>
                        <InputWrapper>
                          <DollarSign>$</DollarSign>
                          <Input
                            type="number"
                            name="healthcareCosts"
                            value={formData.healthcareCosts}
                            onChange={handleInputChange}
                            placeholder="500"
                            min="0"
                          />
                        </InputWrapper>
                      </InputGroup>
                      
                      <InputGroup>
                        <Label>Additional Goals or Concerns</Label>
                        <TextArea
                          name="additionalGoals"
                          value={formData.additionalGoals}
                          onChange={handleInputChange}
                          placeholder="Tell us about any specific retirement goals, concerns, or questions you have..."
                        />
                      </InputGroup>
                    </>
                  )}
                  
                  <ButtonGroup>
                    {currentStep > 1 && (
                      <SecondaryButton type="button" onClick={handlePrevious}>
                        Previous
                      </SecondaryButton>
                    )}
                    <SubmitButton 
                      type="submit" 
                      disabled={loading}
                      style={{ marginLeft: currentStep === 1 ? 'auto' : '0' }}
                    >
                      {currentStep === totalSteps ? (loading ? 'CALCULATING...' : 'CALCULATE RESULTS') : 'NEXT'}
                    </SubmitButton>
                  </ButtonGroup>
                </Form>
              ) : (
                <div>
                  <ResultsSection>
                    <h2 style={{ color: 'white', marginBottom: '2rem' }}>Your Personalized Retirement Plan</h2>
                    
                    <ResultsGrid>
                      <ResultCard>
                        <ResultLabel>Years to Retirement</ResultLabel>
                        <ResultValue>{results.yearsToRetirement} years</ResultValue>
                      </ResultCard>
                      
                      <ResultCard>
                        <ResultLabel>Total Retirement Needed</ResultLabel>
                        <ResultValue>{formatCurrency(results.retirementNeeded)}</ResultValue>
                      </ResultCard>
                      
                      <ResultCard>
                        <ResultLabel>Projected Savings</ResultLabel>
                        <ResultValue>{formatCurrency(results.projectedSavings)}</ResultValue>
                      </ResultCard>
                      
                      <ResultCard>
                        <ResultLabel>Savings Gap</ResultLabel>
                        <ResultValue style={{ color: results.savingsGap > 0 ? '#EF4444' : '#10B981' }}>
                          {formatCurrency(results.savingsGap)}
                        </ResultValue>
                      </ResultCard>
                      
                      <ResultCard>
                        <ResultLabel>Current Monthly</ResultLabel>
                        <ResultValue>{formatCurrency(Number(formData.monthlyContribution))}</ResultValue>
                      </ResultCard>
                      
                      <ResultCard>
                        <ResultLabel>Recommended Monthly</ResultLabel>
                        <ResultValue>{formatCurrency(results.recommendedMonthly)}</ResultValue>
                      </ResultCard>
                    </ResultsGrid>
                    
                    <div style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '1.5rem', borderRadius: '8px', marginTop: '2rem' }}>
                      <h3 style={{ color: 'white', marginBottom: '1rem' }}>Your Retirement Analysis Summary</h3>
                      <p style={{ lineHeight: '1.6', color: '#B8C6DB' }}>
                        Based on your inputs, you'll need approximately <strong style={{ color: 'white' }}>{formatCurrency(results.retirementNeeded)}</strong> to 
                        maintain your desired lifestyle in retirement. With your current savings and contribution rate, 
                        you're projected to have <strong style={{ color: 'white' }}>{formatCurrency(results.projectedSavings)}</strong> by retirement.
                      </p>
                      {results.savingsGap > 0 && (
                        <p style={{ lineHeight: '1.6', color: '#B8C6DB', marginTop: '1rem' }}>
                          To close the gap of <strong style={{ color: '#EF4444' }}>{formatCurrency(results.savingsGap)}</strong>, we recommend increasing 
                          your monthly contribution to <strong style={{ color: 'white' }}>{formatCurrency(results.recommendedMonthly)}</strong>. 
                          An Annuity Specialist will contact you shortly to discuss personalized strategies to help you achieve your retirement goals.
                        </p>
                      )}
                    </div>
                  </ResultsSection>
                  
                  {success && (
                    <SuccessMessage>
                      ✓ Your results have been sent! A retirement specialist will contact you within 24 hours.
                    </SuccessMessage>
                  )}
                  
                  <ButtonGroup>
                    <SecondaryButton
                      type="button"
                      onClick={() => {
                        setCurrentStep(1);
                        setResults(null);
                        setSuccess(false);
                      }}
                    >
                      Start New Calculation
                    </SecondaryButton>
                    <SubmitButton
                      type="button"
                      onClick={() => window.print()}
                    >
                      Print Results
                    </SubmitButton>
                  </ButtonGroup>
                </div>
              )}
              
              <Divider>OR</Divider>
              
              <AlternativeSection>
                <AlternativeText>Want to check annuity performance instead?</AlternativeText>
                <AlternativeButton onClick={switchToBacktester}>
                  Try the AI Backtester
                </AlternativeButton>
              </AlternativeSection>
            </>
          )}
          </ContentCard>
        </MainContent>

        <BacktesterFooter>
          <p>© {new Date().getFullYear()} Annuity Answers. All rights reserved.</p>
          <p>
            <Link to="/privacy-policy">Privacy Policy</Link> | {' '}
            <Link to="/terms">Terms of Service</Link>
          </p>
        </BacktesterFooter>
      </BacktesterWrapper>
    </SharedLayout>
  );
};